import React, { useMemo } from "react";
import useSWR from "swr";
import { useHistory } from "react-router-dom";
import { H3 } from "../../../../components/Typography/Typography";
import { Table } from "../../../../components/Table/Table";
import { IDMobile } from "../../../../components/IDs/IDs";
import {
  StatusLeft,
  getProductStatusColor,
  getProductStatusText,
} from "../../../../components/Status/Status";
import { endpoints } from "../../../../endpoints";
import { TablePlaceholder, useStoreState } from "../../../../util/util";
import { useRoutePath } from "../../../../util/Routing";
import type { MinimalProductSummary } from "../../OrganizationPage/ProductsList/ProductsList";
import { useTranslation } from "react-i18next";
import type {
  PIMProduct,
  ProductStatusType,
} from "../../../../types/types.PIM";

export const ProductVariants: React.FC<{ product: PIMProduct }> = ({
  product,
}) => {
  const { tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();
  const history = useHistory();

  const isMaster = product.pim_product_type === "master";

  const { data, error } = useSWR<{
    data: MinimalProductSummary[];
  }>(
    isMaster
      ? endpoints.v2_tenants_id_pim_products_id_variants(
          tenant_id,
          product.product_number ??
            (product.status === "staged"
              ? product.published_product_id
              : product.id)
        )
      : null
  );
  const isLoading = !data && !error;

  const columns = useMemo(
    () => [
      {
        header: t("Product ID"),
        accessorKey: "product_number",
        id: "product_number",
        cell: (cell: any) => <IDMobile>{cell.getValue()}</IDMobile>,
      },
      {
        header: t("Name"),
        accessorKey: "name",
        id: "name",
        cell: (cell: any) => cell.getValue() || "--",
      },
      {
        header: t("Status"),
        accessorKey: "status",
        id: "status",
        cell: (cell: any) => {
          const row = cell.row.original as MinimalProductSummary;
          const status: ProductStatusType =
            (row.display_status as ProductStatusType) ??
            (row.status as ProductStatusType) ??
            "draft";
          return (
            <StatusLeft
              color={getProductStatusColor(status)}
              text={getProductStatusText(status, t)}
              textStyle={{ fontSize: "13px" }}
              productStatusType="product"
            />
          );
        },
      },
    ],
    [t]
  );

  const tableData = useMemo(() => {
    if (!data?.data || !isMaster) return [];
    return data.data.map((variant) => ({
      ...variant,
      rowURL: `${adminPath}/pim/products/${variant.product_number}`,
    }));
  }, [adminPath, data?.data, isMaster]);

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    const rowIndex = e.currentTarget.getAttribute("data-rowindex");
    if (rowIndex !== null && rowIndex !== undefined) {
      const idx = Number(rowIndex);
      const row = tableData[idx];
      if (row && row.rowURL) {
        history.push(row.rowURL);
      }
    }
  };

  return isMaster ? (
    <div className="product-collection-card">
      <H3>{t("Product Variants")}</H3>
      <Table
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        error={error}
        rowClick={handleRowClick}
        rowHover={(row: any) => row.rowURL}
        Placeholder={TablePlaceholder({})}
      />
    </div>
  ) : null;
};
